import React from 'react';
import { Toast } from 'primereact/toast';
import AppLoading from '../app-loading/app-loading';

interface CustomerReportDataTableProps {
  columns: { name: string }[];
  data: Record<string, string | number>[];
  isLoading?: boolean;
}

const getColumnHeatColor = (value: number, min: number, max: number) => {
  const colors = [
    '#F0F7F4',
    '#C4E3D5', 
    '#87C7A1', 
    '#4A9D73',
    '#1A6B4B',
  ];

  const ratio = (value - min) / (max - min || 1);
  const colorIndex = Math.min(
    Math.floor(ratio * (colors.length - 1)),
    colors.length - 2
  );
  const frac = ratio * (colors.length - 1) - colorIndex;


  const hexToRgb = (hex: string) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return [r, g, b];
  };

  const rgb1 = hexToRgb(colors[colorIndex]);
  const rgb2 = hexToRgb(colors[colorIndex + 1]);

  const r = Math.round(rgb1[0] + frac * (rgb2[0] - rgb1[0]));
  const g = Math.round(rgb1[1] + frac * (rgb2[1] - rgb1[1]));
  const b = Math.round(rgb1[2] + frac * (rgb2[2] - rgb1[2]));

  return `rgb(${r}, ${g}, ${b})`;
};

const getTextColor = (bgColor: string): string => {
  const [r, g, b] = bgColor.match(/\d+/g)!.map(Number);
  const brightness = (r * 299 + g * 587 + b * 114) / 1000;
  return brightness > 150 ? '#1A3A32' : '#F5FBF8'; 
};

const CustomerReportDataTable: React.FC<CustomerReportDataTableProps> = ({
  columns,
  data,
  isLoading,
}) => {
  if (isLoading) return <AppLoading />;


  const filterPayload = {
  pageNumber: 1,
  pageSize: 10,
  customerId: null,
  salesExecutiveId: null
};

  const columnExtremes = columns.map(col => {
    const values = data.map(row => 
      typeof row[col.name] === 'number' ? Number(row[col.name]) : null
    ).filter((val): val is number => val !== null);
    
    return {
      min: Math.min(...values),
      max: Math.max(...values),
    };
  });

  return (
    <div className="col-12">
      <Toast ref={React.createRef()} />
      <div className="card border-noround p-3 shadow-sm">
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: `repeat(${columns.length}, 1fr)`,
            gap: '8px',
          }}
        >
      
          {columns.map((col, colIndex) => (
            <div
              key={col.name}
              style={{
                fontWeight: 600,
                padding: '12px',
                background: 'linear-gradient(135deg, #2B7A6A, #1F4E43)',
                color: '#F5FBF8',
                borderRadius: '8px',
                textAlign: 'center',
                fontSize: '0.95rem',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                borderTopLeftRadius: colIndex === 0 ? '12px' : '8px',
                borderTopRightRadius: colIndex === columns.length - 1 ? '12px' : '8px',
                letterSpacing: '0.5px',
              }}
            >
              {col.name.replace(/_/g, ' ')}
            </div>
          ))}

          {data.map((row, rowIndex) =>
            columns.map((col, colIndex) => {
              const val = row[col.name];
              const isNum = typeof val === 'number';
              const { min, max } = columnExtremes[colIndex];

              if (colIndex === 0) {
                return (
                  <div
                    key={`${rowIndex}-${colIndex}`}
                    style={{
                      padding: '12px',
                      background: 'linear-gradient(135deg, #3A8B7A, #2B7A6A)',
                      color: '#F5FBF8',
                      borderRadius: '8px',
                      fontWeight: 600,
                      textAlign: 'center',
                      fontSize: '0.95rem',
                      cursor: 'default',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                      transition: 'all 0.2s ease',
                    }}
                    onMouseEnter={e => {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 4px 8px rgba(31,111,97,0.2)';
                    }}
                    onMouseLeave={e => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.08)';
                    }}
                  >
                    {val}
                  </div>
                );
              }

              const bgColor = isNum ? getColumnHeatColor(val, min, max) : '#F8FCFA';
              const textColor = isNum ? getTextColor(bgColor) : '#1A3A32';

              return (
                <div
                  key={`${rowIndex}-${colIndex}`}
                  style={{
                    padding: '12px',
                    backgroundColor: bgColor,
                    background: isNum ? bgColor : '#F8FCFA',
                    color: textColor,
                    fontWeight: isNum ? 600 : 500,
                    borderRadius: '8px',
                    textAlign: 'center',
                    fontSize: '0.95rem',
                    transition: 'all 0.2s ease',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                    cursor: 'default',
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.transform = 'scale(1.03)';
                    e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.1)';
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.transform = 'scale(1)';
                    e.currentTarget.style.boxShadow = '0 1px 2px rgba(0,0,0,0.05)';
                  }}
                >
                  {isNum ? val.toLocaleString() : val}
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

export default CustomerReportDataTable;